import Page from "@/lib/components/Page";
import {Pressable, StyleSheet, View} from "react-native";
import {useChat} from "@/lib/components/ChatProvider";
import SpinnerPage from "@/lib/components/SpinnerPage";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useAccount} from "@/lib/components/AccountProvider";
import {KeyboardAwareScrollView, KeyboardAwareScrollViewRef} from "react-native-keyboard-controller";
import ChatInput from "@/lib/components/ChatInput";
import ChatBubble from "@/lib/components/ChatBubble";
import Carousel from "@/lib/components/Carousel";
import {chat} from "@/lib/proto";
import PrimaryText from "@/lib/components/PrimaryText";
import {Color} from "@/lib/styles";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Avatar = chat.Avatar;

export default function Index() {
    const {currentChat, chatError, markMessageAsRead} = useChat();
    const {account} = useAccount();
    const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);

    const [previewThumbnail, setPreviewThumbnail] = useState<string | null>(null);
    const [numShownMessages, setNumShownMessages] = useState(100);
    const [scrolledToBottom, setScrolledToBottom] = useState(true);

    const messages = useMemo(() => {
        return currentChat?.messages.entries().toArray().slice(-numShownMessages) || [];
    }, [currentChat, numShownMessages])

    const pendingMessages = useMemo(() => {
        return currentChat?.pendingMessages.entries().toArray().filter(([_key, msg]) => msg.reaction.length === 0) || [];
    }, [currentChat])

    const numTruncatedMessages = useMemo(() => {
        return (currentChat?.messages.size || 0) - messages.length;
    }, [currentChat, messages]);

    const lastMessage = useMemo(() => {
        if (messages.length === 0) {
            return;
        }
        return messages[messages.length - 1][1];
    }, [messages]);

    const reactions = useCallback((messageID: string) => {
        return currentChat?.reactions.get(messageID) || new Map<string, Avatar[]>();
    }, [currentChat])

    const allThumbnails = useMemo(() => {
        return messages
            .flatMap(([_key, msg]) => msg.thumbnailIds)
            .filter(t => t.length > 0);
    }, [messages]);

    useEffect(() => {
        // could trigger also on currentChat?.reactions
        if (!scrollRef.current || !scrolledToBottom) {
            return;
        }
        scrollRef.current.scrollToEnd();
    }, [scrollRef, currentChat, currentChat?.messages, currentChat?.pendingMessages, scrolledToBottom]);

    if (!currentChat || !account) {
        return <SpinnerPage/>
    }

    return (
        <Page>
            <Carousel thumbnailId={previewThumbnail} thumbnails={allThumbnails} onThumbnailClick={setPreviewThumbnail}/>
            <KeyboardAwareScrollView
                // refreshControl={}
                // maintainVisibleContentPosition={}
                ref={scrollRef}
                focusable={false}
                indicatorStyle={"white"}
                persistentScrollbar={false}
                contentInsetAdjustmentBehavior={"always"}
                contentContainerStyle={styles.chatLog}
                stickyHeaderIndices={[0]}
                stickyHeaderHiddenOnScroll={true}
                removeClippedSubviews={true}
                onScroll={(event) => {
                    const {layoutMeasurement, contentOffset, contentSize} = event.nativeEvent;
                    const isBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height;
                    if (isBottom && lastMessage !== undefined) {
                        markMessageAsRead(lastMessage.chatId, lastMessage.messageId);
                    }
                    if (!scrolledToBottom && isBottom) {
                        setScrolledToBottom(true);
                    } else if (scrolledToBottom && !isBottom) {
                        setScrolledToBottom(false);
                    }
                }}
            >
                {numTruncatedMessages > 0 &&
                    <Pressable style={styles.truncatedBanner} onPress={() => setNumShownMessages(numShownMessages + 100)}>
                        <PrimaryText>{numTruncatedMessages} tidligere meldinger</PrimaryText>
                    </Pressable>
                }
                {messages.map(([key, message]) =>
                    <ChatBubble
                        key={key}
                        message={message}
                        onPressThumbnail={setPreviewThumbnail}
                        reactions={reactions(message.messageId)}
                    />
                )}
                {!!chatError &&
                    <ErrorMessage error={chatError}/>
                }
                {pendingMessages.length > 0 &&
                    <View style={styles.pendingMessages}>
                        {pendingMessages.map(([key, message]) =>
                            <ChatBubble
                                key={key}
                                message={message}
                                reactions={new Map()}
                                onPressThumbnail={setPreviewThumbnail}
                            />
                        )}
                    </View>
                }
            </KeyboardAwareScrollView>
            <View>
                <ChatInput autofocus={true} chat={currentChat.chat}/>
            </View>
        </Page>
    )
}

export const styles = StyleSheet.create({
    truncatedBanner: {
        flexDirection: "row",
        justifyContent: "center",
        alignContent: "center",
        backgroundColor: Color.BackgroundContainer,
        padding: 8,
        borderRadius: 8,
    },
    chatLog: {
        flexDirection: "column",
        gap: 4,
    },
    pendingMessages: {
        opacity: 0.4,
        gap: 4,
    },
})